// Shared utility functions, styles and types between listing components
import { SearchFilterFragment } from '@/__generated__/graphql';
import { Props as FilterProps } from '@/components/MultipleSelectFilter';
import { container, grid, mediaQueries } from '@theme/lib';
import styled from 'styled-components';

// Styles
export const ListingGridContainer = styled.div`
  ${grid}
  ${container}
  gap: 1.5rem 1rem;

  ${mediaQueries.lg} {
    gap: 2rem 2rem;
  }
`;

export const StyledFilterWrapper = styled.div`
  display: flex;
  flex-flow: column;
  grid-column: 1 / span 5;

  &:last-child {
    margin-bottom: 1.5rem;

    ${mediaQueries.lg} {
      margin-bottom: 2.5rem;
    }
  }

  ${mediaQueries.md} {
    grid-column: 2 / span 3;
    flex-flow: row;
    justify-content: flex-end;
  }

  ${mediaQueries.lg} {
    grid-column: 2 / span 8;
  }
`;

export const StyledFilterList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 1rem;

  ${mediaQueries.md} {
    flex-direction: row;
    flex-wrap: wrap;
  }
`;

export const StyledCardWrapper = styled.li`
  list-style: none;
  grid-column: 1 / span 5;

  ${mediaQueries.md} {
    grid-column: 2 / span 3;
  }

  ${mediaQueries.lg} {
    &:nth-child(2n + 1) {
      grid-column: 2 / span 4;
    }

    &:nth-child(2n) {
      grid-column: 6 / span 4;
    }
  }
`;

// types
export type SelectedFilters = {
  [filterType: string]: Set<string>;
};

// Helper functions
// Get currently applied filters from the url
export const getAppliedFilters = (href: string, filterTypes: string[]): SelectedFilters => {
  const params = new URL(href).searchParams;
  const appliedFilters: SelectedFilters = {};
  for (const filterType of filterTypes) {
    const values = params.getAll(filterType);
    const flatValues = values.map((value) => value.split(',')).flat();
    appliedFilters[filterType] = new Set(flatValues);
  }
  return appliedFilters;
};

// Change page parameter in url
export const changePage = (href: string, pageNumber: number): void => {
  const url = new URL(href);
  const page = Math.max(pageNumber, 1);
  // Use a 1 based index. The page handler converts this to 0-based index.
  if (page > 1) {
    url.searchParams.set('page', page.toString());
  }
  else {
    url.searchParams.delete('page');
  }
  window.location.href = url.href;
};

// Apply selected filters
export const applyFilters = (href: string, filters: SelectedFilters): void => {
  const url = new URL(href);
  const origSearchParams = url.searchParams.toString();
  for (const [filterType, values] of Object.entries(filters)) {
    if (values.size) {
      url.searchParams.set(filterType, Array.from(values).join(','));
    } else {
      url.searchParams.delete(filterType);
    }
  }

  // Set filters as cleared for scrolling functionality
  if ([...filters[Object.keys(filters)[0]]].length === 0) localStorage.setItem("filtersCleared", "true");

  // The filters have changed. Reset pagination to avoid confusing results.
  if (origSearchParams !== url.searchParams.toString()) {
    url.searchParams.delete('page');
  }
  window.location.href = url.href;
};

// Format filter data into the Filter component's props type
type FormatFiltersArg = {
  href: string,
  filters: SearchFilterFragment[];
  selectedFilters: SelectedFilters;
  appliedFilters: SelectedFilters;
  onCheckboxChange: (filterType: string, value: string) => void;
  onApply: (href: string, selectedFilters: SelectedFilters) => void;
  onClose: (filterType: string) => void;
  title?: string;
  searchFilterMap?: { tid: string; label: string;}[]
};

export const formatFilters = ({
  href,
  filters,
  selectedFilters,
  appliedFilters,
  onCheckboxChange,
  onApply,
  onClose,
  title,
  searchFilterMap,
}: FormatFiltersArg): FilterProps[] => {
  const filterProps = filters.map((filter) => {
    const options = filter.values
      ? filter.values.map((value) => ({
          name: value,
          checked: selectedFilters[filter.name].has(value),
        })).map((v) => {
        if (searchFilterMap) {
          const id = searchFilterMap.findIndex((labels) => labels.tid === v.name);
          if (id !== -1) {
            return {
              ...v,
              label: searchFilterMap[id].label
            }
          }
        }
        return v;
      })
      : [];

    // Disable 'Apply Filters' button if the user hasn't made any new selection
    const applyButtonDisabled =
      Object.keys(appliedFilters).length > 0 &&
      appliedFilters[filter.name].size === selectedFilters[filter.name].size &&
      Array.from(appliedFilters[filter.name]).every((value) =>
        selectedFilters[filter.name].has(value)
      );

    return {
      title: title ? title : `Filter by ${filter.name.toLowerCase()}`,
      options,
      applyButtonDisabled,
      onCheckboxChange: (value: string) => onCheckboxChange(filter.name, value),
      onApply: () => onApply(href, selectedFilters),
      onClose: () => onClose(filter.name),
    };
  });

  return filterProps;
};

export const removeFilterFromSelected = (selectedFilters: SelectedFilters, filter: string) => {
  const newFilters: SelectedFilters = {};
  for (const key in selectedFilters) {
    newFilters[key] = new Set(selectedFilters[key]);

    if (newFilters[key].has(filter)) {
      newFilters[key].delete(filter)
    }
  }

  return newFilters
}
